/**
 * @vuelto-surface
 * mobile: n/a
 * desktop: ok
 * strategy: inherit
 */
import React, { useState } from 'react';
import { StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { ticketColors, ticketFonts } from '../theme/public-api';

export type TicketDeskFooterProps = {
  pageIndex: number;
  pageSize: number;
  productCount: number;
  totalCount: number | null;
  hasMore: boolean;
  loading?: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onGoToPage?: (page: number) => void;
  /** Dentro del panel desk: sin borde/radio propio. */
  embedded?: boolean;
  /** Padding horizontal del pie (default 14). POS desk usa gutter de grilla. */
  contentPaddingHorizontal?: number;
};

/** Pie PC ticket: `1–20 de 231` + Anterior / Siguiente (sin chips de page size). */
export function TicketDeskFooter({
  pageIndex,
  pageSize,
  productCount,
  totalCount,
  hasMore,
  loading = false,
  onPreviousPage,
  onNextPage,
  onGoToPage,
  embedded = false,
  contentPaddingHorizontal = 14,
}: TicketDeskFooterProps) {
  const from = productCount === 0 ? 0 : pageIndex * pageSize + 1;
  const to = pageIndex * pageSize + productCount;
  const info = totalCount != null ? `${from}–${to} de ${totalCount}` : `${from}–${to}`;
  const prevDisabled = pageIndex <= 0 || loading;
  const nextDisabled = !hasMore || loading;
  const totalPages = totalCount != null && totalCount > 0 ? Math.ceil(totalCount / pageSize) : null;
  const [goToValue, setGoToValue] = useState('');
  const [inputError, setInputError] = useState(false);

  if (totalCount === 0 && !hasMore) return null;

  // Proto Square no tiene pager: si cabe en una página, no mostramos pie.
  if (totalPages != null && totalPages <= 1 && pageIndex === 0 && !hasMore) return null;

  const handleGoTo = () => {
    const page = parseInt(goToValue, 10);
    if (totalPages == null || goToValue.trim() === '' || Number.isNaN(page) || page < 1) {
      setInputError(true);
      setTimeout(() => setInputError(false), 2000);
      return;
    }
    // Fuera de rango → clamp a la última página (el sufijo «/ N» ya muestra el máximo).
    const target = Math.min(page, totalPages);
    if (page > totalPages) {
      setInputError(true);
      setTimeout(() => setInputError(false), 2000);
    }
    onGoToPage?.(target - 1);
    setGoToValue('');
  };

  return (
    <View
      style={[
        styles.foot,
        embedded && styles.footEmbedded,
        { paddingHorizontal: contentPaddingHorizontal },
      ]}
      accessibilityLabel={`Paginación: ${info}`}
    >
      <Text style={styles.info}>{info}</Text>
      <View style={styles.nav}>
        {onGoToPage && totalPages != null && totalPages > 1 ? (
          <View style={styles.goToWrap}>
            <Text style={styles.goToLabel}>Ir a</Text>
            <TextInput
              style={[styles.goToInput, inputError && styles.goToInputError]}
              value={goToValue}
              onChangeText={(text) => {
                // Solo dígitos — «abc» no puede llegar al submit.
                setGoToValue(text.replace(/[^0-9]/g, ''));
                setInputError(false);
              }}
              onSubmitEditing={handleGoTo}
              keyboardType="numeric"
              inputMode="numeric"
              maxLength={Math.max(2, String(totalPages ?? 99).length)}
              placeholder="pág."
              placeholderTextColor={ticketColors.ink3}
              accessibilityLabel="Ir a página"
              accessibilityHint={`Entre 1 y ${totalPages ?? '—'}`}
              returnKeyType="go"
            />
            <Text style={styles.goToTotal}>/ {totalPages}</Text>
            <TouchableOpacity
              style={[styles.goToBtn, goToValue.trim() === '' && styles.btnDisabled]}
              onPress={handleGoTo}
              disabled={goToValue.trim() === ''}
              accessibilityRole="button"
              accessibilityLabel="Ir a la página indicada"
            >
              <Text style={styles.goToBtnText}>Ir</Text>
            </TouchableOpacity>
          </View>
        ) : null}
        <TouchableOpacity
          style={[styles.btn, prevDisabled && styles.btnDisabled]}
          onPress={onPreviousPage}
          disabled={prevDisabled}
          accessibilityRole="button"
          accessibilityLabel="Página anterior"
        >
          <Text style={styles.btnText}>Anterior</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[styles.btn, nextDisabled && styles.btnDisabled]}
          onPress={onNextPage}
          disabled={nextDisabled}
          accessibilityRole="button"
          accessibilityLabel="Página siguiente"
        >
          <Text style={styles.btnText}>Siguiente</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  foot: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  footEmbedded: {
    borderTopWidth: 0,
    borderRadius: 0,
    marginTop: 0,
    backgroundColor: ticketColors.paper2,
  },
  info: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.mono,
    fontVariant: ['tabular-nums'],
  },
  nav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  goToWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginRight: 6,
  },
  goToLabel: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  goToInput: {
    width: 48,
    height: 36,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    paddingHorizontal: 6,
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.mono,
    textAlign: 'center',
  },
  goToInputError: {
    borderColor: ticketColors.bad,
  },
  goToTotal: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.mono,
  },
  goToBtn: {
    height: 36,
    paddingHorizontal: 12,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
  },
  goToBtnText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  btn: {
    height: 36,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.tile,
    alignItems: 'center',
    justifyContent: 'center',
  },
  btnDisabled: {
    opacity: 0.4,
  },
  btnText: {
    fontSize: 13,
    fontWeight: '600',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
});
